import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'

import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'

import {
  ApixDeaktivoiSopimuksetPyynto,
  ApixDeaktivoiSopimuksetVastaus,
  ApixHaeAsiakkaanLahetetytLaskutPyynto,
  ApixHaeAsiakkaanLahetetytLaskutVastaus,
  ApixLahetettyLasku,
  ApixSopimuksenTiedot,
} from '@lemon/core-firebase/model/apix'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'

export type AsiakasProperties = 'nimi' | 'ytunnus' | 'exportoi' | 'asiakasId'

@Component({
  templateUrl: './apix-laskujen-tiedot.component.html',
  styleUrls: ['./apix-laskujen-tiedot.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakkaanApixLaskujenTiedotComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private _reloadSopimuksetSubject = new BehaviorSubject(1)

  laskutObservable: Observable<ApixLahetettyLasku[]>
  sopimuksetObservable: Observable<ApixSopimuksenTiedot[]>
  readonly ladattu = signal(false)

  constructor(
    private _errorHandler: ErrorHandler,
    private _asiakasService: AsiakasService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _ladataanService: LadataanService,
  ) {}

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnInit() {
    const apixLaskutJaSopimuksetObservable = combineLatest([this._asiakasService.nykyinenAsiakasAvainObservable, this._reloadSopimuksetSubject]).pipe(
      tap(() => {
        this.ladattu.set(false)
      }),
      switchMap(([asiakas, reload]) => {
        if (asiakas) {
          const params: ApixHaeAsiakkaanLahetetytLaskutPyynto = {
            asiakasAvain: asiakas.avain,
          }
          return this._firebaseLemonaid.functionsCall<ApixHaeAsiakkaanLahetetytLaskutPyynto, ApixHaeAsiakkaanLahetetytLaskutVastaus>('apixHaeAsiakkaanLahetetytLaskut', params)
        }
        return of<ApixHaeAsiakkaanLahetetytLaskutVastaus>(null)
      }),
      tap(() => {
        this.ladattu.set(true)
      }),
      lemonShare(),
    )

    this.laskutObservable = apixLaskutJaSopimuksetObservable.pipe(
      map(data => {
        if (!data) {
          return []
        }
        return data.laskut
      }),
    )

    this.sopimuksetObservable = apixLaskutJaSopimuksetObservable.pipe(
      map(data => {
        if (!data) {
          return []
        }
        return data.sopimukset
      }),
    )
  }

  async disableAllContracts() {
    this._ladataanService.aloitaLataaminen()
    try {
      const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
      if (asiakas) {
        const params: ApixDeaktivoiSopimuksetPyynto = {
          asiakasAvain: asiakas.avain,
        }
        const resp = await this._firebaseLemonaid.functionsCall<ApixDeaktivoiSopimuksetPyynto, ApixDeaktivoiSopimuksetVastaus>('apixDeaktivoiAsiakkaanKaikkiSopimukset', params)

        if (!resp || resp.e) {
          throw new Error('Apix sopimusten deaktivointi epäonnistui! ' + resp?.e || 'No response')
        }

        this._reloadSopimuksetSubject.next(Date.now())
        await this._sleep(2000)
      }
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  private _sleep(millis: number): Promise<void> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve()
      }, millis)
    })
  }
}
